import { TRANSLATION_MEMORY_IMPORTS_QUERY } from '@src/gql/translation-memory-gql';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type ApiClient from '../apiClient/ApiClient';
import type {
  EntryPersonRef,
  LanguageRef,
} from './TranslationMemoryEntryRepository';
import type { Client } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

/** Result summary retained on the import record + shown on the results view. */
export interface ImportCounts {
  created: number;
  overwritten: number;
  skipped: number;
  /** Rows the bulk write dropped after an error (PLAT-10871); 0 in a preview. */
  failed: number;
}

/** A collection the import targeted (entries fan out one per collection, E12). */
export interface ImportCollectionRef {
  id: string;
  name: string;
}

/**
 * A retained bulk upload (PLAT-10839 `translation_memory_import`) — provenance
 * for imported entries and the source of the import-history list (E11). `file`
 * is the original spreadsheet; `collections` is the multi-link import target;
 * `counts` is the created/overwritten/skipped summary. `createdAt` comes from
 * Dato record meta.
 */
export interface TranslationMemoryImport {
  id: string;
  fileName: string;
  fileUrl: string | null;
  sourceLanguage: LanguageRef | null;
  translationLanguage: LanguageRef | null;
  collections: ImportCollectionRef[];
  counts: ImportCounts;
  createdByUser?: EntryPersonRef | null;
  createdAt?: string | null;
}

/** Fields written when creating an import record (after the file is uploaded). */
export interface TranslationMemoryImportInput {
  uploadId: string;
  fileName: string;
  sourceLanguage: LanguageRef;
  /** `null` ⇒ the file targeted all languages (E10). */
  translationLanguage: LanguageRef | null;
  /** Original language strings from the file, if any (provenance; DS blank ⇒ ''). */
  sourceLanguageRaw?: string | null;
  translationLanguageRaw?: string | null;
  collectionIds: string[];
  counts: ImportCounts;
}

/** Raw GraphQL shape (nested links) before flattening to `TranslationMemoryImport`. */
interface RawImport {
  id: string;
  fileName: string;
  file: { url: string } | null;
  sourceLanguage: LanguageRef | null;
  translationLanguage: LanguageRef | null;
  collections: ImportCollectionRef[];
  counts: ImportCounts | null;
  createdByUser: EntryPersonRef | null;
  _createdAt: string | null;
}

interface ImportsResponse {
  allTranslationMemoryImports: RawImport[];
}

// Normalize per-field (not just whole-object) so a partial/legacy `counts` JSON
// (e.g. an older record missing `overwritten`) never surfaces `undefined` in the
// history table — the type says `number`, but the stored JSON isn't guaranteed.
const normalizeCounts = (raw: ImportCounts | null): ImportCounts => ({
  created: raw?.created ?? 0,
  overwritten: raw?.overwritten ?? 0,
  skipped: raw?.skipped ?? 0,
  // Records written before PLAT-10871 have no `failed` key — read as 0 rather
  // than requiring a data migration.
  failed: raw?.failed ?? 0,
});

const mapImport = (raw: RawImport): TranslationMemoryImport => ({
  id: raw.id,
  fileName: raw.fileName,
  fileUrl: raw.file?.url ?? null,
  sourceLanguage: raw.sourceLanguage,
  translationLanguage: raw.translationLanguage,
  collections: raw.collections ?? [],
  counts: normalizeCounts(raw.counts),
  createdByUser: raw.createdByUser,
  createdAt: raw._createdAt,
});

/**
 * Read + write `translation_memory_import`. Uploads the original spreadsheet as
 * a Dato asset, then creates the retained record (PLAT-10848 / E11). Mirrors the
 * entry repository idiom: GraphQL read filtered by showcase that **rethrows** on
 * failure (so a failed load surfaces as an error state, not an empty history);
 * `create` returns null on failure (logged with `extractErrorInfo`).
 */
export class TranslationMemoryImportRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger(
    'repository:TranslationMemoryImportRepository',
  );

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  /** All imports for a showcase, newest first. Rethrows on failure. */
  async fetchForShowcase(
    showcaseId: string,
  ): Promise<TranslationMemoryImport[]> {
    try {
      const response = await this.gqlClient.request<ImportsResponse>(
        TRANSLATION_MEMORY_IMPORTS_QUERY,
        { showcaseId },
      );
      return (response.allTranslationMemoryImports ?? []).map(mapImport);
    } catch (error) {
      this.logger.error(
        'Error fetching translation memory imports',
        extractErrorInfo(error),
      );
      throw error;
    }
  }

  /** Upload the original spreadsheet as a Dato asset; returns the upload id. */
  async uploadFile(file: File): Promise<string | null> {
    try {
      const upload = await this.dClient.uploads.createFromFileOrBlob({
        fileOrBlob: file,
        filename: file.name,
      });
      return upload.id;
    } catch (error) {
      this.logger.error(
        'Error uploading translation memory import file',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /** Create + publish the retained import record, stamping the uploader. */
  async create(
    showcaseId: string,
    input: TranslationMemoryImportInput,
    personId: string | null,
  ): Promise<TranslationMemoryImport | null> {
    try {
      const itemType = await this.dClient.itemTypes.find(
        'translation_memory_import',
      );
      const created = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemType.id },
        showcase: showcaseId,
        file: { upload_id: input.uploadId },
        file_name: input.fileName,
        source_language: input.sourceLanguage.id,
        translation_language: input.translationLanguage?.id ?? null,
        source_language_raw: input.sourceLanguageRaw ?? '',
        translation_language_raw: input.translationLanguageRaw ?? '',
        collections: input.collectionIds,
        created_by_user: personId,
        // Dato `json` fields are written as a JSON string (read back as an
        // object via GraphQL) — mirrors StoryRepository/CommentRepository.
        counts: JSON.stringify(input.counts),
      });
      await this.dClient.items.publish(created.id);
      return this.echo(created.id, input, personId);
    } catch (error) {
      this.logger.error(
        'Error creating translation memory import',
        extractErrorInfo(error),
      );
      return null;
    }
  }

  /**
   * Rewrite an existing record's `counts` (PLAT-10871). The record must be
   * created before the entries are written (they link back to it for
   * provenance), so at create time only the preview's projected counts are
   * known; this stamps the real ones once the write has run. Failure is logged
   * and reported as `false` — the entries are already committed, so a stale
   * count must not fail the import.
   */
  async updateCounts(recordId: string, counts: ImportCounts): Promise<boolean> {
    try {
      await this.dClient.items.update(recordId, {
        counts: JSON.stringify(counts),
      });
      await this.dClient.items.publish(recordId);
      return true;
    } catch (error) {
      this.logger.error(
        'Error updating translation memory import counts',
        extractErrorInfo(error),
      );
      return false;
    }
  }

  /**
   * Build the confirmed import from the write input so the store can prepend it
   * to the history without refetching (GraphQL lags CMA writes). Collection
   * names aren't in the input, so `collections` carries only ids here; the next
   * load reconciles names from Dato.
   */
  private echo(
    id: string,
    input: TranslationMemoryImportInput,
    personId: string | null,
  ): TranslationMemoryImport {
    return {
      id,
      fileName: input.fileName,
      fileUrl: null,
      sourceLanguage: input.sourceLanguage,
      translationLanguage: input.translationLanguage,
      collections: input.collectionIds.map(cid => ({ id: cid, name: '' })),
      counts: input.counts,
      createdByUser: personId ? { id: personId } : null,
      createdAt: new Date().toISOString(),
    };
  }
}
